// queryEngine.ts
import { GRAPH, Primitive } from "./queryable-graph"
import {
    AggregateField,
    InferQueryResult,
    OrgScopedResource,
    ProjectScope,
    ResourceName,
    ScopeName,
    ScopeValues,
    SortDirection,
    TimeBucket,
    TypedQueryPayload,
    ValidPath,
} from "./queryableTypes"

interface EngineNode {
    table: string
    alias: string
    scopes: readonly ScopeName[]
    keys?: readonly string[]
    softDelete?: string
    schema: Record<string, string>
    edges?: Record<string, { resource: ResourceName; on: string; joinType?: "inner" | "left" }>
    computed?: Record<string, string | { expr: string; type: Primitive }>
}

function computedExpr(entry: string | { expr: string; type: Primitive }) {
    return typeof entry === "string" ? entry : entry.expr
}

const ALLOWED_OPS = new Set([
    "=",
    "!=",
    ">",
    "<",
    ">=",
    "<=",
    "ILIKE",
    "LIKE",
    "IS NULL",
    "IS NOT NULL",
    "IN",
    "NOT IN",
])

const ALLOWED_AGGS = new Set(["COUNT", "SUM", "AVG", "MIN", "MAX", "JSON_AGG"])
const ALLOWED_DIRS = new Set<SortDirection>(["ASC", "DESC"])
const ALLOWED_TIME_UNITS = new Set(["hour", "day", "week", "month", "quarter", "year"])

const isValidTimezone = (timezone: string) => {
    try {
        new Intl.DateTimeFormat(undefined, { timeZone: timezone })
        return true
    } catch {
        return false
    }
}

export interface SqlResult<T> {
    text: string
    values: unknown[]
    _phantom?: T
}

export function buildGraphQuery<
    R extends ResourceName,
    const S extends ReadonlyArray<ValidPath<R>> | undefined = undefined,
    const A extends ReadonlyArray<AggregateField<R>> | undefined = undefined,
    const T extends ReadonlyArray<TimeBucket<R>> | undefined = undefined,
>(
    payload: TypedQueryPayload<R, S, A, T>,
    scopes: ScopeValues<R>
): SqlResult<InferQueryResult<R, S, A, T>> {
    const engineGraph = GRAPH as Record<ResourceName, EngineNode>

    if ((payload.select?.length ?? 0) > 30) throw new Error("Query exceeds max select fields (30)")
    if ((payload.filters?.length ?? 0) > 20) throw new Error("Query exceeds max filters (20)")
    if ((payload.aggregates?.length ?? 0) > 10) throw new Error("Query exceeds max aggregates (10)")
    if ((payload.timeBuckets?.length ?? 0) > 5)
        throw new Error("Query exceeds max time buckets (5)")
    if ((payload.having?.length ?? 0) > 20) throw new Error("Query exceeds max having clauses (20)")
    if ((payload.orderBy?.length ?? 0) > 10)
        throw new Error("Query exceeds max order by fields (10)")
    if ((payload.existsFilters?.length ?? 0) > 10)
        throw new Error("Query exceeds max exists filters (10)")

    const resource = payload.resource
    const rootNode = engineGraph[resource]
    if (!rootNode) throw new Error(`Unknown resource: ${resource}`)

    const rootKeys: readonly string[] = rootNode.keys || ["id"]

    const selectClauses: string[] = []
    const groupByClauses: string[] = []
    const whereClauses: string[] = []
    const havingClauses: string[] = []
    const orderByClauses: string[] = []
    const params: unknown[] = []
    let paramIdx = 1

    const scopeValues: { org: string; project?: ProjectScope } = scopes

    const scopeClauses = (node: EngineNode, alias: string) =>
        node.scopes
            .map((scope) => {
                if (scope === "org") {
                    params.push(scopeValues.org)
                    return `${alias}.org_id = $${paramIdx++}`
                }
                const project = scopeValues.project
                if (project === undefined)
                    throw new Error(
                        `Resource '${node.table}' declares a project scope but none was supplied`
                    )
                if (project === "all") return null
                params.push(project)
                return `(${alias}.project_id IS NULL OR ${alias}.project_id = ANY($${paramIdx++}))`
            })
            .filter((clause): clause is string => clause !== null)

    whereClauses.push(...scopeClauses(rootNode, rootNode.alias))

    // 1b. SOFT DELETE: Exclude soft-deleted root rows by default
    if (rootNode.softDelete) {
        whereClauses.push(`${rootNode.alias}.${rootNode.softDelete} IS NULL`)
    }

    const joinsToBuild = new Map<
        string,
        { table: string; alias: string; on: string; joinType?: "inner" | "left" }
    >()
    const aggExpressions = new Map<string, string>()

    function resolvePath(fieldPath: string): string {
        const parts = fieldPath.split(".")

        // SECURITY FIX: Prevent DoS via infinite join cycles
        if (parts.length > 6) throw new Error(`Query path too deep: Maximum depth exceeded.`)

        let currentNode: EngineNode = rootNode
        let currentAlias = rootNode.alias

        for (let i = 0; i < parts.length; i++) {
            const part = parts[i]
            const isLastPart = i === parts.length - 1

            // SECURITY FIX: Use hasOwn to prevent prototype pollution attacks
            const isSchemaField = Object.hasOwn(currentNode.schema, part)
            const isComputedField =
                currentNode.computed && Object.hasOwn(currentNode.computed, part)

            if (isSchemaField || isComputedField) {
                if (isLastPart) {
                    if (isComputedField) {
                        return computedExpr(currentNode.computed![part]).replace(
                            /\$\{alias\}/g,
                            currentAlias
                        )
                    }
                    return `${currentAlias}.${part}`
                }
                continue
            }

            const hasEdge = currentNode.edges && Object.hasOwn(currentNode.edges, part)
            if (hasEdge) {
                const edge = currentNode.edges![part]
                const targetNode = engineGraph[edge.resource]
                if (!targetNode) throw new Error(`Broken Graph Config: ${edge.resource}`)

                // SECURITY FIX: Path-dependent aliases to prevent data collisions
                // on queries that traverse the same table twice via different routes.
                const uniqueJoinAlias = `${currentAlias}_${part}`

                if (!joinsToBuild.has(uniqueJoinAlias)) {
                    // Safely swap the static GRAPH aliases for our dynamic path aliases
                    const targetRegex = new RegExp(`\\b${targetNode.alias}\\.`, "g")
                    const sourceRegex = new RegExp(`\\b${currentNode.alias}\\.`, "g")

                    let joinOn = edge.on.replace(targetRegex, `${uniqueJoinAlias}.`)
                    joinOn = joinOn.replace(sourceRegex, `${currentAlias}.`)

                    scopeClauses(targetNode, uniqueJoinAlias).forEach((clause) => {
                        joinOn += ` AND ${clause}`
                    })

                    // 2b. SOFT DELETE: Exclude soft-deleted joined rows by default
                    if (targetNode.softDelete) {
                        joinOn += ` AND ${uniqueJoinAlias}.${targetNode.softDelete} IS NULL`
                    }

                    joinsToBuild.set(uniqueJoinAlias, {
                        table: targetNode.table,
                        alias: uniqueJoinAlias,
                        on: joinOn,
                        joinType: edge.joinType,
                    })
                }

                currentNode = targetNode
                currentAlias = uniqueJoinAlias
                continue
            }
            throw new Error(`Invalid path part '${part}' on node '${currentNode.table}'`)
        }
        throw new Error(`Could not resolve path: ${fieldPath}`)
    }

    const alreadySelected = new Set<string>()

    const isAnalyticalQuery =
        (payload.aggregates && payload.aggregates.length > 0) ||
        (payload.timeBuckets && payload.timeBuckets.length > 0)

    // 3. AUTO-SELECT KEYS
    if (!isAnalyticalQuery) {
        rootKeys.forEach((key) => {
            const col = `${rootNode.alias}.${key}`
            selectClauses.push(col)
            groupByClauses.push(col)
            alreadySelected.add(key)
        })
    }

    // 4. PROCESS SELECTS
    if (payload.select && payload.select.length > 0) {
        payload.select.forEach((field) => {
            if (alreadySelected.has(field)) return

            const dbCol = resolvePath(field)
            const alias = escapeIdentifier(field)

            selectClauses.push(`${dbCol} AS ${alias}`)
            groupByClauses.push(dbCol)
            alreadySelected.add(field)
        })
    }

    // 5. PROCESS TIME BUCKETS (Temporal Grouping)
    if (payload.timeBuckets && payload.timeBuckets.length > 0) {
        payload.timeBuckets.forEach((tb) => {
            if (!ALLOWED_TIME_UNITS.has(tb.unit)) throw new Error(`Invalid time unit: ${tb.unit}`)

            const dbCol = resolvePath(tb.field)
            let expression = `DATE_TRUNC('${tb.unit}', ${dbCol})`

            if (tb.timezone) {
                if (!isValidTimezone(tb.timezone))
                    throw new Error(`Invalid timezone: ${tb.timezone}`)
                expression = `DATE_TRUNC('${tb.unit}', ${dbCol} AT TIME ZONE '${tb.timezone}') AT TIME ZONE '${tb.timezone}'`
            }

            const alias = escapeIdentifier(tb.alias)
            selectClauses.push(`${expression} AS ${alias}`)
            groupByClauses.push(expression)
            aggExpressions.set(tb.alias, expression)
        })
    }

    // 6. PROCESS AGGREGATES
    if (payload.aggregates && payload.aggregates.length > 0) {
        payload.aggregates.forEach((agg) => {
            if (!ALLOWED_AGGS.has(agg.func)) throw new Error(`Invalid func: ${agg.func}`)

            const parts = agg.field.split(".")
            const rootEdgeName = parts[0]
            let expression = ""

            // Strategy B: Subquery (Aggregating across a relation)
            const hasRootEdge = rootNode.edges && Object.hasOwn(rootNode.edges, rootEdgeName)
            if (parts.length > 1 && hasRootEdge) {
                const gatewayEdge = rootNode.edges![rootEdgeName]
                const gatewayNode = engineGraph[gatewayEdge.resource]

                if (!gatewayNode) throw new Error(`Broken Graph: ${gatewayEdge.resource}`)

                // Use path-dependent aliases inside the subquery to avoid collisions with
                // outer-query aliases (e.g. root table "m" leaking into subquery scope).
                const sqPrefix = "_sq_"
                const sqGatewayAlias = `${sqPrefix}${gatewayNode.alias}`

                let currentAlias = sqGatewayAlias
                let currentNode: EngineNode = gatewayNode
                const internalJoins: string[] = []
                let finalCol = ""

                // Remap the gateway alias in the correlated join condition
                const gatewayAliasRe = new RegExp(`\\b${gatewayNode.alias}\\.`, "g")
                const correlatedOn = gatewayEdge.on.replace(gatewayAliasRe, `${sqGatewayAlias}.`)

                const subqueryWhereClauses = [correlatedOn]

                subqueryWhereClauses.push(...scopeClauses(gatewayNode, sqGatewayAlias))

                if (gatewayNode.softDelete) {
                    subqueryWhereClauses.push(`${sqGatewayAlias}.${gatewayNode.softDelete} IS NULL`)
                }

                for (let i = 1; i < parts.length; i++) {
                    const part = parts[i]
                    const isLast = i === parts.length - 1

                    const hasInternalEdge =
                        currentNode.edges && Object.hasOwn(currentNode.edges, part)
                    if (hasInternalEdge) {
                        const edge = currentNode.edges![part]
                        const targetNode = engineGraph[edge.resource]
                        const targetAlias = `${sqPrefix}${targetNode.alias}`

                        let joinOn = edge.on
                            .replace(
                                new RegExp(`\\b${targetNode.alias}\\.`, "g"),
                                `${targetAlias}.`
                            )
                            .replace(
                                new RegExp(`\\b${currentNode.alias}\\.`, "g"),
                                `${currentAlias}.`
                            )

                        scopeClauses(targetNode, targetAlias).forEach((clause) => {
                            joinOn += ` AND ${clause}`
                        })

                        if (targetNode.softDelete) {
                            joinOn += ` AND ${targetAlias}.${targetNode.softDelete} IS NULL`
                        }

                        const joinKeyword =
                            (edge.joinType ?? "left") === "inner" ? "INNER JOIN" : "LEFT JOIN"

                        internalJoins.push(
                            `${joinKeyword} ${targetNode.table} ${targetAlias} ON ${joinOn}`
                        )

                        currentNode = targetNode
                        currentAlias = targetAlias
                    } else if (isLast) {
                        const hasComputed =
                            currentNode.computed && Object.hasOwn(currentNode.computed, part)
                        if (hasComputed) {
                            finalCol = computedExpr(currentNode.computed![part]).replace(
                                /\$\{alias\}/g,
                                currentAlias
                            )
                        } else {
                            finalCol = `${currentAlias}.${part}`
                        }
                    } else {
                        throw new Error(
                            `Cannot resolve '${part}' inside aggregate on ${currentNode.table}`
                        )
                    }
                }

                // Handle 1-hop aggregates
                if (!finalCol && parts.length === 2) {
                    const part = parts[1]
                    const hasComputed =
                        gatewayNode.computed && Object.hasOwn(gatewayNode.computed, part)
                    if (hasComputed) {
                        finalCol = computedExpr(gatewayNode.computed![part]).replace(
                            /\$\{alias\}/g,
                            sqGatewayAlias
                        )
                    } else {
                        finalCol = `${sqGatewayAlias}.${part}`
                    }
                }

                if (!finalCol)
                    throw new Error(`Aggregate path '${agg.field}' did not end in a column`)

                // 1. Determine inner and outer functions
                let innerFunc = agg.func
                let outerFunc = agg.func

                if (agg.func === "COUNT") {
                    innerFunc = "COUNT"
                    outerFunc = "SUM" // The outer aggregation sums the sub-counts of each parent
                } else if (agg.func === "AVG" || agg.func === "JSON_AGG") {
                    throw new Error(`${agg.func} is not supported on cross-table relations.`)
                }

                // 2. Build the inner subquery
                const subquery = `(
SELECT ${innerFunc}(${finalCol})
FROM ${gatewayNode.table} ${sqGatewayAlias}
${internalJoins.length > 0 ? internalJoins.join("\n                ") : ""}
WHERE ${subqueryWhereClauses.join(" AND ")}
)`

                // 3. Wrap the subquery in the outer aggregate so Postgres groups it correctly
                if (outerFunc === "SUM") {
                    expression = `COALESCE(SUM(${subquery}), 0)`
                } else {
                    expression = `${outerFunc}(${subquery})`
                }
            } else {
                // Strategy A: Local Aggregate
                const dbCol = resolvePath(agg.field)
                expression = `${agg.func}(${dbCol})`
            }

            aggExpressions.set(agg.alias, expression)
            const alias = escapeIdentifier(agg.alias)
            selectClauses.push(`${expression} AS ${alias}`)
        })
    }

    // 7. FILTERS (WHERE)
    const filterClause = (dbCol: string, f: { op: string; value: unknown }) => {
        if (!ALLOWED_OPS.has(f.op)) throw new Error(`Invalid operator: ${f.op}`)

        if (f.op === "IS NOT NULL" || f.op === "IS NULL") return `${dbCol} ${f.op}`

        if (f.op === "IN" || f.op === "NOT IN") {
            const anyOp = f.op === "IN" ? "= ANY" : "!= ALL"
            params.push(f.value)
            return `${dbCol} ${anyOp}($${paramIdx++})`
        }

        params.push(f.value)
        return `${dbCol} ${f.op} $${paramIdx++}`
    }

    if (payload.filters) {
        payload.filters.forEach((f) => {
            if (f.disabled) return
            whereClauses.push(filterClause(resolvePath(f.field), f))
        })
    }

    if (payload.filterGroups) {
        if (payload.filterGroups.length > 10)
            throw new Error("Query exceeds max filter groups (10)")

        const groupClauses = payload.filterGroups.flatMap((group) => {
            if (group.disabled) return []
            const active = group.filters.filter((f) => !f.disabled)
            if (active.length === 0) return []
            if (active.length > 20)
                throw new Error("Query exceeds max filters in a filter group (20)")
            return [`(${active.map((f) => filterClause(resolvePath(f.field), f)).join(" AND ")})`]
        })

        if (groupClauses.length > 0) whereClauses.push(`(${groupClauses.join(" OR ")})`)
    }

    // 8. EXISTS SUBFILTERS
    if (payload.existsFilters) {
        payload.existsFilters.forEach((ef) => {
            if (ef.disabled) return
            if (!rootNode.edges || !Object.hasOwn(rootNode.edges, ef.relation))
                throw new Error(`Unknown relation '${ef.relation}' on ${rootNode.table}`)

            const edge = rootNode.edges[ef.relation]
            const targetNode = engineGraph[edge.resource]
            if (!targetNode) throw new Error(`Broken graph: ${edge.resource}`)

            // Sub-filter DoS Protection
            if ((ef.filters?.length ?? 0) > 20)
                throw new Error("Query exceeds max exists sub-filters (20)")

            const subAlias = `${rootNode.alias}_ex_${ef.relation}`

            const targetRegex = new RegExp(`\\b${targetNode.alias}\\.`, "g")
            const sourceRegex = new RegExp(`\\b${rootNode.alias}\\.`, "g")
            const joinOn = edge.on
                .replace(targetRegex, `${subAlias}.`)
                .replace(sourceRegex, `${rootNode.alias}.`)

            const existsWhere = [joinOn]

            existsWhere.push(...scopeClauses(targetNode, subAlias))
            if (targetNode.softDelete) {
                existsWhere.push(`${subAlias}.${targetNode.softDelete} IS NULL`)
            }

            if (ef.filters) {
                ef.filters.forEach((f) => {
                    if (f.disabled) return
                    if (!ALLOWED_OPS.has(f.op)) throw new Error(`Invalid operator: ${f.op}`)

                    // Safely support both real schema columns AND computed columns
                    const isSchema = Object.hasOwn(targetNode.schema, f.field)
                    const isComputed =
                        targetNode.computed && Object.hasOwn(targetNode.computed, f.field)

                    if (!isSchema && !isComputed)
                        throw new Error(`Unknown field '${f.field}' on ${targetNode.table}`)

                    // Resolve the actual field expression
                    const fieldExpr = isComputed
                        ? computedExpr(targetNode.computed![f.field]).replace(
                              /\$\{alias\}/g,
                              subAlias
                          )
                        : `${subAlias}.${f.field}`

                    if (f.op === "IS NULL" || f.op === "IS NOT NULL") {
                        existsWhere.push(`${fieldExpr} ${f.op}`)
                    } else if (f.op === "IN" || f.op === "NOT IN") {
                        const anyOp = f.op === "IN" ? "= ANY" : "!= ALL"
                        existsWhere.push(`${fieldExpr} ${anyOp}($${paramIdx++})`)
                        params.push(f.value)
                    } else {
                        existsWhere.push(`${fieldExpr} ${f.op} $${paramIdx++}`)
                        params.push(f.value)
                    }
                })
            }

            whereClauses.push(
                `EXISTS (SELECT 1 FROM ${targetNode.table} ${subAlias} WHERE ${existsWhere.join(" AND ")})`
            )
        })
    }

    // 9. HAVING
    if (payload.having) {
        payload.having.forEach((f) => {
            if (!ALLOWED_OPS.has(f.op)) throw new Error(`Invalid operator: ${f.op}`)
            const expression = aggExpressions.get(f.field)
            if (!expression) throw new Error(`Having clause references unknown alias: ${f.field}`)

            if (f.op === "IS NOT NULL" || f.op === "IS NULL") {
                havingClauses.push(`${expression} ${f.op}`)
            } else if (f.op === "IN" || f.op === "NOT IN") {
                const anyOp = f.op === "IN" ? "= ANY" : "!= ALL"
                havingClauses.push(`${expression} ${anyOp}($${paramIdx++})`)
                params.push(f.value)
            } else {
                havingClauses.push(`${expression} ${f.op} $${paramIdx++}`)
                params.push(f.value)
            }
        })
    }

    // 10. ORDER BY
    if (payload.orderBy) {
        payload.orderBy.forEach((sort) => {
            if (!ALLOWED_DIRS.has(sort.dir)) throw new Error(`Invalid direction: ${sort.dir}`)
            if (aggExpressions.has(sort.field)) {
                const alias = escapeIdentifier(sort.field)
                orderByClauses.push(`${alias} ${sort.dir}`)
            } else {
                const dbCol = resolvePath(sort.field)
                orderByClauses.push(`${dbCol} ${sort.dir}`)
            }
        })
    }

    // 11. ASSEMBLE SQL
    const sqlParts = []

    if (payload.distinctOn) {
        const distinctCol = resolvePath(payload.distinctOn)
        sqlParts.push(`SELECT DISTINCT ON (${distinctCol}) ${selectClauses.join(", ")}`)
    } else {
        sqlParts.push(`SELECT ${selectClauses.join(", ")}`)
    }

    sqlParts.push(`FROM ${rootNode.table} ${rootNode.alias}`)

    joinsToBuild.forEach((join) => {
        const joinKeyword = (join.joinType ?? "outer") === "inner" ? "INNER JOIN" : "LEFT JOIN"
        sqlParts.push(`${joinKeyword} ${join.table} ${join.alias} ON ${join.on}`)
    })

    if (whereClauses.length > 0) {
        sqlParts.push(`WHERE ${whereClauses.join(" AND ")}`)
    }

    if (payload.aggregates && payload.aggregates.length > 0 && groupByClauses.length > 0) {
        sqlParts.push(`GROUP BY ${groupByClauses.join(", ")}`)
    }

    if (havingClauses.length > 0) {
        sqlParts.push(`HAVING ${havingClauses.join(" AND ")}`)
    }

    if (orderByClauses.length > 0) {
        sqlParts.push(`ORDER BY ${orderByClauses.join(", ")}`)
    }

    if (typeof payload.limit === "number") {
        const cleanLimit = Math.floor(Math.max(0, payload.limit))
        sqlParts.push(`LIMIT $${paramIdx++}`)
        params.push(cleanLimit)
    }

    if (typeof payload.offset === "number") {
        const cleanOffset = Math.floor(Math.max(0, payload.offset))
        sqlParts.push(`OFFSET $${paramIdx++}`)
        params.push(cleanOffset)
    }

    return {
        text: sqlParts.join(" "),
        values: params,
    }
}

function escapeIdentifier(str: string): string {
    return `"${str.replace(/"/g, '""')}"`
}

// sound by the OrgScopedResource constraint; TS can't reduce ScopeValues while R is generic
export const orgScope = <R extends OrgScopedResource>(org: string) => ({ org }) as ScopeValues<R>

export function createQuery<
    R extends ResourceName,
    const S extends ReadonlyArray<ValidPath<R>> | undefined = undefined,
    const A extends ReadonlyArray<AggregateField<R>> | undefined = undefined,
    const T extends ReadonlyArray<TimeBucket<R>> | undefined = undefined,
>(payload: TypedQueryPayload<R, S, A, T>): TypedQueryPayload<R, S, A, T> {
    return payload
}

type MasterPayloadUnion = {
    [K in ResourceName]: TypedQueryPayload<
        K,
        ReadonlyArray<ValidPath<K>>,
        ReadonlyArray<AggregateField<K>>,
        ReadonlyArray<TimeBucket<K>>
    >
}[ResourceName]

export function defineQuery<Args extends unknown[], const P extends MasterPayloadUnion>(
    builder: (...args: Args) => P
): (...args: Args) => P {
    return builder
}
